import { useEffect, useRef } from 'react';

export default function EnvironmentalActivities() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const activities = [
    {
      icon: 'ri-recycle-line',
      title: '廃棄物削減と資源循環',
      description: '養殖施設から発生する有機廃棄物を肥料として再利用し、地域の農業と連携した循環型システムを構築しています。',
      metrics: [
        { label: '廃棄物削減率', value: '92%' },
        { label: '資源再利用率', value: '88%' }
      ]
    },
    {
      icon: 'ri-drop-line',
      title: '水資源の保全',
      description: '閉鎖循環型養殖システムにより、水の使用量を最小限に抑え、地域の貴重な水資源を保全しています。',
      metrics: [
        { label: '水使用量削減', value: '95%' },
        { label: '水質浄化率', value: '99%' }
      ]
    },
    {
      icon: 'ri-plant-line',
      title: '生物多様性の保護',
      description: '地域の自然環境と調和した事業運営により、生態系への影響を最小限に抑え、生物多様性の保全に努めています。',
      metrics: [
        { label: '生態系保全面積', value: '15ha' },
        { label: '在来種保護', value: '100%' }
      ]
    },
    {
      icon: 'ri-seedling-line',
      title: '森林保全活動',
      description: '地域の森林保全活動に参加し、植林や間伐作業を通じて、CO₂吸収源の維持・拡大に貢献しています。',
      metrics: [
        { label: '植林本数', value: '2,500本' },
        { label: 'CO₂吸収量', value: '45t/年' }
      ]
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 animate-on-scroll opacity-0 translate-y-8 transition-all duration-1000">
          <div className="inline-block px-4 py-2 bg-emerald-100 rounded-full mb-4">
            <span className="text-sm font-semibold text-emerald-700 tracking-wide">ENVIRONMENTAL ACTIVITIES</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">環境保全活動</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            地域の自然環境を守り、次世代に豊かな環境を引き継ぐための<br />多様な環境保全活動に取り組んでいます。
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {activities.map((activity, index) => (
            <div
              key={index}
              className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-1000 bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-8 border border-emerald-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className={`${activity.icon} text-3xl text-white`}></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{activity.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{activity.description}</p>
                  <div className="grid grid-cols-2 gap-4">
                    {activity.metrics.map((metric, idx) => (
                      <div key={idx} className="bg-white rounded-xl p-4 border border-emerald-100">
                        <div className="text-2xl font-bold text-emerald-600 mb-1">{metric.value}</div>
                        <div className="text-sm text-gray-600">{metric.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 animate-on-scroll opacity-0 translate-y-8 transition-all duration-1000">
          <div className="relative h-96 w-full rounded-2xl overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/75a289cb8200c888543d596d0b5cfcd8.jpg"
              alt="環境保全活動"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-10 text-white">
              <h3 className="text-3xl font-bold mb-3">地域と共に取り組む環境保全</h3>
              <p className="text-lg text-white/90 max-w-3xl">
                地域住民や自治体と連携し、持続可能な環境保全活動を推進しています。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}